/**
 * "✓ Mark follow-up done" box — web (2026-10-07, Manak). Opened from
 * My Follow-ups "✓ Mark done" and Team Follow-ups ✓. Same box as the phone:
 *   - "Recent calls with <name>" (last 7 days, newest first). The newest
 *     connected call is picked by itself; its wrap-up outcome, note and next
 *     date are reused.
 *   - "— or log it without a call —": the Wrap Up's Connected / Not connected
 *     toggle + outcome chips + Create Quote / Prospect dealer.
 *   - Next follow-up row ("From the call's wrap-up" / "Set by outcome") with
 *     a date picker; Cancel / Save & mark done at the bottom.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PhoneIncoming, PhoneOutgoing, X } from 'lucide-react';
import { api } from '@/utils/api';
import {
  buildDoneBody,
  callDuration,
  callWhen,
  fetchRecentCalls,
  goManual,
  initialDoneBoxState,
  nextFollowUp,
  nextWhen,
  pickCall,
  pickOutcome,
  saveBlocker,
  saveDone,
  selectedCall,
  WRAP_UP_OUTCOMES,
  type DoneBoxState,
  type DoneSaved,
} from './followUpDoneApi';

export interface FollowUpDoneTarget {
  name: string;
  leadId: string | null;
  /** Team Follow-ups: the tc_followups row clicked. */
  followUpRowId?: string | null;
}

const CONNECTED_TONES = ['bg-emerald-50 text-emerald-800', 'bg-rose-50 text-rose-800', 'bg-violet-50 text-violet-800', 'bg-amber-50 text-amber-800'];

function toLocalInput(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 5.5 * 3600_000);
  return d.toISOString().slice(0, 16);
}
function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}:00.000+05:30`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function FollowUpDoneDialog({ target, onClose, onSaved }: {
  target: FollowUpDoneTarget;
  onClose: () => void;
  onSaved: (saved: DoneSaved) => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<DoneBoxState | null>(target.leadId ? null : initialDoneBoxState({ calls: [], defaultCallId: null }));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNext, setEditingNext] = useState(false);

  useEffect(() => {
    if (!target.leadId) return;
    let live = true;
    fetchRecentCalls(target.leadId)
      .then((p) => { if (live) setState(initialDoneBoxState(p)); })
      .catch(() => {
        if (!live) return;
        setLoadError('Could not load recent calls — log it without a call.');
        setState(initialDoneBoxState({ calls: [], defaultCallId: null }));
      });
    return () => { live = false; };
  }, [target.leadId]);

  const call = state ? selectedCall(state) : null;
  const callHasOutcome = !!call?.outcome;
  const manualFaded = !!call && callHasOutcome;
  const chips = useMemo(() => (state?.outcomes ?? WRAP_UP_OUTCOMES).filter((o) => o.connected === (state?.connected ?? true)), [state]);
  const next = state ? nextFollowUp(state) : { at: null, source: 'none' as const, label: null };
  const blocker = state ? saveBlocker(state) : 'Loading';

  const save = async (action: 'create_quote' | 'prospect_dealer' | null = null) => {
    if (!state) return;
    if (!action && blocker) { setError(blocker); return; }
    if (action === 'prospect_dealer' && !window.confirm(`Mark ${target.name || 'this lead'} as a prospect dealer? This moves the lead out of your sales pipeline.`)) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveDone({ leadId: target.leadId, followUpRowId: target.followUpRowId ?? null }, buildDoneBody(state, action));
      const leadId = saved.leadImportId ?? target.leadId;
      if (action === 'prospect_dealer' && leadId) {
        await api.post(`/leads/${leadId}/mark-dealer-prospect`).catch(() => setError('Done saved, but marking as prospect dealer failed.'));
      }
      onSaved(saved);
      if (action === 'create_quote' && leadId) navigate(`/proposals/new?leadId=${encodeURIComponent(leadId)}`);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Could not mark the follow-up done.');
    } finally {
      setSaving(false);
    }
  };

  const nextSub = next.source === 'call' ? "From the call's wrap-up · click to change"
    : next.source === 'outcome' ? `Set by outcome · ${next.label} · click to change`
    : next.source === 'picked' ? 'Set by you · click to change'
    : 'No follow-up set — click to schedule';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Mark follow-up done">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl" data-tour="followup-done-box">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">✓ Mark follow-up done · {target.name}</h2>
            <p className="text-xs text-gray-500">{state && state.calls.length ? 'Which call was this follow-up?' : 'How did it go?'}</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-4">
          {!state ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading recent calls…</div>
          ) : <>
            {loadError && <p className="text-xs text-amber-700">{loadError}</p>}
            {state.calls.length > 0 ? (
              <section data-testid="done-recent-calls">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent calls (last 7 days)</p>
                <ul className="space-y-2">
                  {state.calls.map((c) => {
                    const on = c.id === state.selectedCallId;
                    return (
                      <li key={c.id}>
                        <button type="button" data-testid={`done-call-${c.id}`} aria-pressed={on}
                          onClick={() => setState(pickCall(state, c.id))}
                          className={`w-full rounded-xl border px-3 py-2 text-left text-sm ${on ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <span className="flex items-center gap-2">
                            {c.direction === 'incoming' ? <PhoneIncoming className="h-3.5 w-3.5 text-gray-500" /> : <PhoneOutgoing className="h-3.5 w-3.5 text-gray-500" />}
                            <span className="font-medium text-gray-900">{callWhen(c.at)} · {callDuration(c.durationSec)} · {c.direction === 'incoming' ? 'Incoming' : 'Outgoing'}{c.agentName ? ` · by ${c.agentName}` : ''}</span>
                            {c.outcome
                              ? <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${c.outcome.connected === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-800'}`}>{c.outcome.label}</span>
                              : <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">No wrap-up</span>}
                          </span>
                          {c.notes && <span className="mt-1 block truncate text-xs text-gray-600">“{c.notes}”</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <p className="my-3 text-center text-xs text-gray-400">— or log it without a call —</p>
              </section>
            ) : (
              <p data-testid="done-no-calls" className="text-sm text-gray-600">No call with {target.name} in the last 7 days</p>
            )}

            <section data-testid="done-manual" className={manualFaded ? 'opacity-40' : undefined}
              onClickCapture={() => { if (manualFaded) setState(goManual(state)); }}>
              {call && !callHasOutcome && <p className="mb-2 text-xs font-medium text-amber-700">This call has no wrap-up — choose how it went</p>}
              <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 text-sm font-semibold">
                {[true, false].map((v) => (
                  <button key={String(v)} type="button" aria-pressed={!manualFaded && state.connected === v}
                    onClick={() => setState((s) => s && ({ ...s, connected: v, code: null, pickedNext: null }))}
                    className={`rounded-lg py-2 ${!manualFaded && state.connected === v ? (v ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800') : 'text-gray-500'}`}>
                    {v ? 'Connected' : 'Not connected'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {chips.map((o, i) => {
                  const on = state.code === o.code;
                  return (
                    <button key={o.code} type="button" data-testid={`done-outcome-${o.code}`} aria-pressed={on}
                      onClick={() => setState((s) => s && pickOutcome(s, o.code))}
                      className={`rounded-xl px-3 py-2 text-left text-sm font-semibold ${o.connected ? CONNECTED_TONES[i % CONNECTED_TONES.length] : 'bg-gray-100 text-gray-800'} ${on ? 'ring-2 ring-current' : ''}`}>
                      {on ? '✓ ' : ''}{o.label}
                    </button>
                  );
                })}
                {state.connected && target.leadId && <>
                  <button type="button" disabled={saving} onClick={() => void save('create_quote')} className="rounded-xl bg-orange-500 px-3 py-2 text-left text-sm font-semibold text-white">Create Quote</button>
                  <button type="button" disabled={saving} onClick={() => void save('prospect_dealer')} className="rounded-xl bg-orange-500 px-3 py-2 text-left text-sm font-semibold text-white">Prospect dealer</button>
                </>}
              </div>
              <input type="text" value={state.notes} placeholder="✎ Quick notes…" maxLength={1000}
                onChange={(e) => setState((s) => s && ({ ...s, notes: e.target.value }))}
                className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm" />
            </section>

            <section className="rounded-xl border border-orange-200 bg-orange-50/40 px-3 py-2">
              <button type="button" data-testid="done-next" onClick={() => setEditingNext((v) => !v)} className="w-full text-left">
                <span className="block text-sm font-semibold text-gray-900">{next.at ? `Next follow-up ✓ ${nextWhen(next.at)}` : 'Next follow-up: none'}</span>
                <span className="block text-xs text-gray-500">{nextSub}</span>
              </button>
              {editingNext && (
                <div className="mt-2 flex items-center gap-2">
                  <input type="datetime-local" aria-label="Schedule follow-up" defaultValue={next.at ? toLocalInput(next.at) : ''}
                    onChange={(e) => { const iso = fromLocalInput(e.target.value); setState((s) => s && ({ ...s, pickedNext: iso, nextCleared: !iso })); }}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                  <button type="button" className="text-xs text-gray-500 underline" onClick={() => setState((s) => s && ({ ...s, pickedNext: null, nextCleared: true }))}>No follow-up</button>
                </div>
              )}
            </section>
          </>}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex gap-3 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700">Cancel</button>
          <button type="button" data-testid="done-save" disabled={saving || !state} onClick={() => void save()}
            className="flex-[2] rounded-xl bg-emerald-700 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {saving ? 'Saving…' : '✓ Save & mark done'}
          </button>
        </div>
      </div>
    </div>
  );
}
